/**
 * PreiseTile — Navy Feature-Card mit Amber-Preis (Sprint-16 Mockup 4c).
 *
 * Signature-Moment der Apartment-Overview-Page:
 *   - Navy-Card mit Paper-Text (dark-variant)
 *   - Big-Serif Preis in AMBER — warmer Money-Signal, matches den Wordmark-○ und
 *     die Direct-Booking-Pillen im Kalender. Editorial-Continuity quer durch die
 *     App: Amber = Money/Warmth-Marker, Sealwax = Identity-Marker.
 *   - Divider paper/10
 *   - KV-Rows für Cleaning-Fee + Währung
 *
 * Revision 2026-07-02: Sealwax auf Navy (4.2:1) war WCAG-AA-passes aber
 * ästhetisch dark-in-dark. Amber auf Navy (8.5:1 AAA) brennt statt zu
 * verschwimmen und schließt den Amber-Signature-Faden.
 *
 * „ARI bearbeiten →"-Link am Boden mit hover-amber (Signature-Repeat).
 */
import { Tag } from 'lucide-react';
import type { PropertyResponse } from '@/shared/api';
import { formatCurrency } from '@/shared/lib/format';
import { BentoTile } from './BentoTile';

function currencySymbol(code: string): string {
  switch (code.toUpperCase()) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    case 'GBP':
      return '£';
    case 'CHF':
      return 'Fr';
    default:
      return code;
  }
}

export function PreiseTile({ property }: { property: PropertyResponse }) {
  const rate = property.baseNightlyRate;
  const hasRate = rate != null && rate > 0;
  const symbol = currencySymbol(property.currency);

  return (
    <BentoTile
      to="/properties/$id/preise"
      propertyId={property.id}
      icon={<Tag size={13} strokeWidth={2} />}
      eyebrow="Preise"
      cta="ARI bearbeiten"
      variant="dark"
    >
      <div className="flex flex-col gap-4">
        {/* Hero-Price */}
        <div>
          <div className="text-paper/50 text-[10px] font-semibold uppercase tracking-[0.14em]">
            Default / Nacht
          </div>
          <div className="text-amber mt-1 font-serif text-5xl font-normal tabular-nums leading-none">
            {hasRate ? (
              <>
                {Math.round(rate)}
                <span className="ml-1 text-4xl">{symbol}</span>
              </>
            ) : (
              <span className="text-paper/40">—</span>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-paper/10 border-t" />

        {/* KV-Rows */}
        <dl className="flex flex-col gap-2 text-[13px]">
          <div className="flex items-baseline justify-between">
            <dt className="text-paper/70">Cleaning-Fee</dt>
            <dd className="text-paper font-mono tabular-nums">
              {formatCurrency(property.cleaningFee, property.currency)}
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-paper/70">Währung</dt>
            <dd className="text-paper font-mono">{property.currency}</dd>
          </div>
        </dl>
      </div>
    </BentoTile>
  );
}
